import { useState } from 'react';
import { useMobile } from '../../hooks/useMobile';
import { useAuthStore } from '../../stores/auth';
import AppearanceTab from './AppearanceTab';
import MessagingTab from './MessagingTab';
import AuditLogTab from './AuditLogTab';

type SettingsTab = 'appearance' | 'messaging' | 'audit';

interface Props {
  onClose: () => void;
}

export default function SettingsPanel({ onClose }: Props) {
  const isMobile = useMobile();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const tabs: Array<{ key: SettingsTab; label: string; icon: string }> = [
    { key: 'appearance', label: 'Appearance', icon: '🎨' },
    ...(isAdmin ? [{ key: 'messaging' as const, label: 'Messaging', icon: '💬' }] : []),
    { key: 'audit', label: 'Audit Log', icon: '📋' },
  ];

  const [tab, setTab] = useState<SettingsTab>('appearance');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,.55)',
        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--vs1)',
        border: isMobile ? 'none' : '1px solid var(--shell-border)',
        borderRadius: isMobile ? '12px 12px 0 0' : 8,
        width: isMobile ? '100vw' : '90vw',
        maxWidth: isMobile ? undefined : 920,
        height: isMobile ? '92vh' : undefined,
        maxHeight: isMobile ? undefined : '88vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,.6)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: isMobile ? '14px 16px 10px' : '14px 20px',
          borderBottom: '1px solid var(--shell-border)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Gear icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--v2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--shell-text)' }}>
              Settings
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--shell-xs)',
              fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1,
              padding: '4px 8px', borderRadius: 3,
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 2, padding: '8px 12px',
          borderBottom: '1px solid var(--shell-border)', flexShrink: 0,
        }}>
          {tabs.map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                background: tab === key ? 'rgba(29,158,117,.15)' : 'transparent',
                color: tab === key ? 'var(--v2)' : 'var(--shell-xs)',
                border: 'none', borderRadius: 4, padding: '7px 16px',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all .12s',
              }}
            >
              <span style={{ fontSize: '0.9rem' }}>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {tab === 'appearance' && <AppearanceTab />}
          {tab === 'messaging' && isAdmin && <MessagingTab />}
          {tab === 'audit' && <AuditLogTab />}
        </div>
      </div>
    </div>
  );
}
